import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { getPromptById, getPromptWorkspacesByPromptId } from "@/db/prompts"
import { v4 as uuidv4 } from "uuid"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('realtime.prompts')

type Row = Tables<'prompts'>
type WsRow = Tables<'prompt_workspaces'>

const appliesToWorkspace = async (workspaceId:string | null, id: string): Promise<boolean> => {
	if (!workspaceId) return false
	const promptWorkspaces = await getPromptWorkspacesByPromptId(supabase, id)
	return promptWorkspaces.workspaces.some(ws => ws.id === workspaceId)
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 * @param workspaceId
 * @param setPrompts
 * @returns
 */
export function makePromptChangeHandler(
	setPrompts: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `prompt-changes-${uuid}`,
		channelName: `prompt-changes-${uuid}`,
		events: ['UPDATE'],
		schema: 'public',
		table: 'prompts',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			if (payload.eventType === 'UPDATE') {
				const prompt = payload.new as Row
				//here not checking for workspace since will update only if found and it avoid async db call
				setPrompts(prev => {
					const exists = prev.some(p => p.id === prompt.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling prompt update:${printObject(prompt)}`)
					return prev.map(p => p.id === prompt.id ? prompt : p)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}

/**
 * This method doesn't handle update since the relationship join table is not designed to mutate in that way
 * @param workspaceId
 * @param setPrompts
 * @returns
 */
export function makePromptWorkspaceHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setPrompts: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<WsRow> {
	const uuid = uuidv4()
	return {
		key: `prompt-ws-changes-${uuid}`,
		channelName: `prompt-ws-changes-${uuid}`,
		events: ['INSERT', 'DELETE'],
		schema: 'public',
		table: 'prompt_workspaces',
		handler: async (payload: RealtimePostgresChangesPayload<WsRow>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") {
				const promptWs = payload.new as WsRow
				const id = promptWs.prompt_id
				if (await appliesToWorkspace(workspaceId, id)) {
					const prompt = await getPromptById(supabase, id)
					if (prompt) {
						setPrompts(prev => {
							if (prev.some(p => p.id === prompt.id)) return prev
							logger.info(`Handling prompt Insert:${printObject(prompt)}`)
							return [...prev, prompt]
						})
					}
				}
			}
			else if (payload.eventType == "DELETE") {
				const promptWs = payload.old as WsRow
				const id = promptWs.prompt_id
				setPrompts(prev => {
					const exists = prev.some(p => p.id === id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling prompt Delete:${id}`)
					return prev.filter(p => p.id !== id)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}